import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  User,
  ChevronDown,
  
  
  UserPlus,
  LogIn,
  Menu,
 
  Sparkles,
  Scale,
  Stethoscope
} from 'lucide-react';
import logo from '../../assets/trueAdvisoLogo/trueAdvisologofull.jpeg';

export function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileExpertsOpen, setIsMobileExpertsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 🟢 Expert subcategory links
  const expertCategories = [
    { label: 'Astrology', to: '/experts/astrology', icon: Sparkles },
    { label: 'Lawyer', to: '/experts/lawyer', icon: Scale },
    { label: 'Doctor', to: '/experts/doctor', icon: Stethoscope },
  ];

  const isExpertsActive = location.pathname.startsWith('/experts');

  const signupItems = [
    { label: 'Sign Up as User', icon: UserPlus, path: '/user-signup' },
    { label: 'Sign Up as Expert', icon: UserPlus, path: '/expert-signup' },
  ];

  const loginItems = [
    { label: 'User Login', icon: LogIn, path: '/user-login' },
    { label: 'Expert Login', icon: LogIn, path: '/expert-login' },
  ];

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative text-sm lg:text-base transition-colors flex items-center ${
      isActive ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3">
        <div className="flex items-center justify-between">

          {/* Logo */}
          <NavLink to="/">
            <img src={logo} alt="trueAdviso" className="h-12 sm:h-16 w-auto object-contain" />
          </NavLink>

          {/* 🟢 Desktop Menu */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <NavLink to="/" className={navClass}>
              {({ isActive }) => (
                <>
                  Home
                  {isActive && <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600" />}
                </>
              )}
            </NavLink>

            <NavLink to="/categories" className={navClass}>
              {({ isActive }) => (
                <>
                  Categories
                  {isActive && <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600" />}
                </>
              )}
            </NavLink>

            {/* 🟢 EXPERTS HOVER DROPDOWN (Non-clickable Parent) */}
            <div className="relative group py-2">
              <button
                type="button"
                className={`flex items-center gap-1 text-sm lg:text-base transition-colors cursor-default ${
                  isExpertsActive ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Experts</span>
                <ChevronDown className="w-4 h-4 text-gray-400 group-hover:rotate-180 transition-transform duration-200" />
              </button>

              {/* Hover Dropdown */}
              <div className="absolute left-0 top-full pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 py-2 overflow-hidden">
                  {expertCategories.map((subItem) => (
                    <NavLink
                      key={subItem.label}
                      to={subItem.to}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive
                            ? 'text-blue-600 bg-blue-50/80 font-semibold'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      {subItem.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/live-sessions" className={navClass}>
              {({ isActive }) => (
                <>
                  Live Sessions
                  {isActive && <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600" />}
                </>
              )}
            </NavLink>

            <NavLink to="/blogs" className={navClass}>
              {({ isActive }) => (
                <>
                  Blogs
                  {isActive && <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600" />}
                </>
              )}
            </NavLink>

            <NavLink to="/about" className={navClass}>
              {({ isActive }) => (
                <>
                  About
                  {isActive && <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-blue-600" />}
                </>
              )}
            </NavLink>
          </nav>

          {/* Right Side (Avatar/Account Menu) */}
          <div className="relative">
            <button
              onMouseEnter={() => setIsDropdownOpen(true)}
              onClick={() => setIsDropdownOpen((p) => !p)}
              className="hidden md:flex items-center gap-2 px-3 py-1 rounded-xl hover:bg-gray-50 transition"
            >
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs text-gray-600">Account</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <button onClick={() => setIsDropdownOpen((p) => !p)} className="md:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-6 h-6 text-gray-700" />
            </button>

            {isDropdownOpen && (
              <div onMouseLeave={() => setIsDropdownOpen(false)} className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                
                {/* 🟢 Mobile Navigation Links */}
                <div className="md:hidden border-b border-gray-100 pb-2 mb-2">
                  <NavLink to="/" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50">
                    Home
                  </NavLink>

                  <NavLink to="/categories" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50">
                    Categories
                  </NavLink>

                  {/* 🟢 Mobile Collapsible Experts Accordion */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setIsMobileExpertsOpen((prev) => !prev)}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-blue-50"
                    >
                      <span>Experts</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isMobileExpertsOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isMobileExpertsOpen && (
                      <div className="bg-gray-50/80 py-1 pl-4">
                        {expertCategories.map((sub) => (
                          <NavLink
                            key={sub.label}
                            to={sub.to}
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50/50 rounded-lg"
                          >
                            <sub.icon className="w-3.5 h-3.5 text-gray-400" />
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>

                  <NavLink to="/live-sessions" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50">
                    Live Sessions
                  </NavLink>
                  <NavLink to="/blogs" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50">
                    Blogs
                  </NavLink>
                  <NavLink to="/about" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50">
                    About
                  </NavLink>
                </div>

                <div className="pb-2">
                  {signupItems.map((item) => (
                    <button key={item.label} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50" onClick={() => { setIsDropdownOpen(false); navigate(item.path); }}>
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="border-t border-gray-200 my-2" />
                {loginItems.map((item) => (
                  <button key={item.label} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50" onClick={() => { setIsDropdownOpen(false); navigate(item.path); }}>
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}